// Conversation-history variant of Chat.tsx: same chat experience, plus a left-hand
// panel of past conversations. Split out under /chat_history so the production
// route can stay untouched while this is tested.

import {
    Checkbox,
    DefaultButton,
    Panel,
    Slider,
    SpinButton,
    TextField,
    Modal,
    IconButton,
    Dropdown,
    IDropdownOption,
    TooltipHost,
    Icon
} from "@fluentui/react";
import { useEffect, useRef, useState } from "react";
import logo from "../../assets/logo-noglow.png";
import forbidden_logo from "../../assets/alexandria_inspector_1.png";
import styles from "../chat/Chat.module.css";

import {
    FileAttachment,
    ChatAppResponse,
    ResponseContext,
    Thoughts,
    Citation,
    CitationType,
    configApiV1,
    GPT4VInput,
    RetrievalMode,
    VectorFieldOptions,
    ApiError,
    loadPowerplantConfigV2,
    PowerplantInfo,
    getFilterOptionsApi,
    FilterOptionsResponse,
    listConversationsApiV2,
    getConversationMessagesApiV2,
    ConversationSummary,
    ErrorDTO
} from "../../api";
import { getHeaders } from "../../api/api";
import { KnowledgeSource } from "../../components/FilterSettings";
import { useGetToken, useIsLoggedIn } from "../../authConfig";
import { AnalysisPanel, AnalysisPanelTabs } from "../../components/AnalysisPanel";
import { Answer, AnswerError, AnswerLoading } from "../../components/Answer";
import { ClearChatButton } from "../../components/ClearChatButton";
import { ConversationSidebar } from "../../components/ConversationSidebar";
import { ExampleList } from "../../components/Example";
import { FilterPowerplantSettings } from "../../components/FilterPowerplantSettings";
import { FilterSettings } from "../../components/FilterSettings";
import { QuestionInput } from "../../components/QuestionInput";
import { UserChatMessage } from "../../components/UserChatMessage";
import { Spinner } from "../../components/Spinner";
import { usePowerPlant } from "../../components/powerplant";
import { PowerplantModal } from "../../components/PowerplantModal";
import { ChangePowerplantButton } from "../../components/ChangePowerplantButton";
import FeedbackModal from "../../components/FeedbackModal/FeedbackModal";
import React from "react";

const SHOW_TOOL_CALL_CHIPS = false;

interface ReasoningToolCall {
    id: string;
    name: string;
    args: string;
    done: boolean;
}

interface ReasoningBlockProps {
    toolCalls: ReasoningToolCall[];
    isDone: boolean;
}

interface ReasoningNode {
    id: string;
    name: string;
    reasoning: string;
    children: ReasoningNode[];
}

function extractRawReasoning(tc: ReasoningToolCall): string | undefined {
    try {
        const args = JSON.parse(tc.args);
        if (typeof args.reasoning === "string" && args.reasoning.trim()) return args.reasoning;
    } catch {
        // args JSON not complete yet while streaming; try to pull out a partial "reasoning" value so it grows live
        const match = tc.args.match(/"reasoning"\s*:\s*"((?:[^"\\]|\\.)*)/);
        if (match) return match[1].replace(/\\"/g, '"').replace(/\\n/g, "\n");
    }
    return undefined;
}

// Sub-agent tool calls are namespaced by the backend as "<agentName>:<originalId>" (see
// namespace_event in agui_input.py). Use that prefix to nest a sub-agent's own internal
// tool calls (e.g. search_document_index) under the parent agent node (e.g. SharepointAgent).
function buildReasoningTree(toolCalls: ReasoningToolCall[], lastReasoningRef: React.MutableRefObject<Record<string, string>>): ReasoningNode[] {
    const byId = new Map(toolCalls.map(tc => [tc.id, tc]));
    const childIdsByParentId = new Map<string, string[]>();
    const topLevelIds: string[] = [];

    for (const tc of toolCalls) {
        const colonIndex = tc.id.indexOf(":");
        const prefix = colonIndex >= 0 ? tc.id.slice(0, colonIndex) : undefined;
        const parent = prefix ? toolCalls.find(p => p.name === prefix && p.id !== tc.id) : undefined;
        if (parent) {
            const list = childIdsByParentId.get(parent.id) ?? [];
            list.push(tc.id);
            childIdsByParentId.set(parent.id, list);
        } else {
            topLevelIds.push(tc.id);
        }
    }

    const toNode = (id: string): ReasoningNode | undefined => {
        const tc = byId.get(id);
        if (!tc) return undefined;
        const rawReasoning = extractRawReasoning(tc);
        // Only cache genuine reasoning text, never the "…" streaming placeholder, so a
        // node without a real "reasoning" arg (e.g. load_skill) can still be filtered out.
        if (rawReasoning) lastReasoningRef.current[id] = rawReasoning;
        const reasoning = rawReasoning ?? lastReasoningRef.current[id] ?? (!tc.done ? "…" : undefined);
        const children = (childIdsByParentId.get(id) ?? []).map(toNode).filter((n): n is ReasoningNode => !!n);
        // load_skill has no "reasoning" arg of its own; skip it unless it has genuine reasoning
        // text or nested children worth showing.
        if (tc.name === "load_skill" && !lastReasoningRef.current[id]?.trim() && children.length === 0) {
            return undefined;
        }
        return { id, name: tc.name, reasoning: reasoning ?? "", children };
    };

    return topLevelIds.map(toNode).filter((n): n is ReasoningNode => !!n);
}

const ReasoningNodeView: React.FC<{ node: ReasoningNode; depth: number; expandedNodes: Record<string, boolean>; onToggle: (id: string) => void }> = ({
    node,
    depth,
    expandedNodes,
    onToggle
}) => {
    const expanded = expandedNodes[node.id] ?? true;
    return (
        <div className={styles.reasoningNode} style={{ marginLeft: depth * 14 }}>
            <button className={styles.reasoningNodeHeader} onClick={() => onToggle(node.id)} type="button">
                <Icon iconName={expanded ? "ChevronDown" : "ChevronRight"} />
                <span>{node.name}</span>
            </button>
            {expanded && (
                <>
                    {node.reasoning && <div className={styles.reasoningText}>{node.reasoning}</div>}
                    {node.children.map(child => (
                        <ReasoningNodeView key={child.id} node={child} depth={depth + 1} expandedNodes={expandedNodes} onToggle={onToggle} />
                    ))}
                </>
            )}
        </div>
    );
};

// Seeded from "is the run over", never from "has the agent produced text yet": an agent
// that streams a preface before calling its tools (typical from the second turn on, once
// it has conversation context) used to mount this already collapsed, hiding every
// TOOL_CALL_* event that followed until the turn was done.
const ReasoningBlock: React.FC<ReasoningBlockProps> = ({ toolCalls, isDone }) => {
    const [collapsed, setCollapsed] = useState<boolean>(isDone);
    const [expandedNodes, setExpandedNodes] = useState<Record<string, boolean>>({});
    const prevIsDoneRef = useRef<boolean>(isDone);
    const lastReasoningRef = useRef<Record<string, string>>({});

    useEffect(() => {
        if (isDone && !prevIsDoneRef.current) {
            setCollapsed(true);
        }
        prevIsDoneRef.current = isDone;
    }, [isDone]);

    const nodes = buildReasoningTree(toolCalls, lastReasoningRef);

    if (nodes.length === 0) return null;

    const toggleNode = (id: string) => setExpandedNodes(prev => ({ ...prev, [id]: !(prev[id] ?? true) }));

    return (
        <div className={styles.reasoningContainer}>
            <button className={styles.reasoningHeader} onClick={() => setCollapsed(c => !c)} type="button">
                <Icon iconName={collapsed ? "ChevronRight" : "ChevronDown"} />
                <span>Reasoning</span>
            </button>
            {!collapsed && (
                <div className={styles.reasoningBody}>
                    {nodes.map(n => (
                        <ReasoningNodeView key={n.id} node={n} depth={0} expandedNodes={expandedNodes} onToggle={toggleNode} />
                    ))}
                </div>
            )}
        </div>
    );
};

const EMPTY_CONTEXT: ResponseContext = {
    data_points: [],
    followup_questions: null,
    thoughts: [],
    sharepoint_uris: {},
    citations: [],
    powerplant: ""
};

function makeResponse(content: string, context: Partial<ResponseContext> = {}): ChatAppResponse {
    return {
        choices: [
            {
                index: 0,
                session_state: null,
                message: { content, role: "assistant" },
                context: { ...EMPTY_CONTEXT, ...context }
            }
        ]
    };
}

interface ChatHistoryProps {
    gdprAccepted?: boolean;
}

const ChatHistory: React.FC<ChatHistoryProps> = ({ gdprAccepted = false }) => {
    const [isConfigPanelOpen, setIsConfigPanelOpen] = useState(false);
    const [promptTemplate, setPromptTemplate] = useState<string>("");
    const [temperature, setTemperature] = useState<number>(0.3);
    const [minimumRerankerScore, setMinimumRerankerScore] = useState<number>(0);
    const [minimumSearchScore, setMinimumSearchScore] = useState<number>(0);
    const [retrieveCount, setRetrieveCount] = useState<number>(10);
    const [retrievalMode, setRetrievalMode] = useState<RetrievalMode>(RetrievalMode.Hybrid);
    const [filters, setFilters] = useState<{ name: string; displayName: string; values: { text: string; key: string }[] }[]>([]);
    const [exampleQuestions, setExampleQuestions] = useState<string[]>([]);
    const [selectedFilters, setSelectedFilters] = useState<Record<string, string[]>>({});
    const [useSemanticRanker, setUseSemanticRanker] = useState<boolean>(true);
    const [shouldStream, setShouldStream] = useState<boolean>(true);
    const [useSemanticCaptions, setUseSemanticCaptions] = useState<boolean>(false);
    const [excludeCategory, setExcludeCategory] = useState<string>("");
    const [useSuggestFollowupQuestions, setUseSuggestFollowupQuestions] = useState<boolean>(true);
    const [vectorFieldList, setVectorFieldList] = useState<VectorFieldOptions[]>([VectorFieldOptions.Both]);
    const [useOidSecurityFilter, setUseOidSecurityFilter] = useState<boolean>(false);
    const [useGroupsSecurityFilter, setUseGroupsSecurityFilter] = useState<boolean>(false);
    const [gpt4vInput, setGPT4VInput] = useState<GPT4VInput>(GPT4VInput.TextAndImages);
    const [useGPT4V, setUseGPT4V] = useState<boolean>(true);

    const lastQuestionRef = useRef<string>("");
    const lastQuestionFilesRef = useRef<string[]>([]);
    const [conversationId, setConversationId] = useState<string>(() => crypto.randomUUID());
    const chatMessageStreamEnd = useRef<HTMLDivElement | null>(null);
    const abortRef = useRef<AbortController | null>(null);

    const [isLoading, setIsLoading] = useState<boolean>(false);
    const [isStreaming, setIsStreaming] = useState<boolean>(false);
    const [error, setError] = useState<unknown>();

    const [activeCitation, setActiveCitation] = useState<string>();
    const [activeAnalysisPanelTab, setActiveAnalysisPanelTab] = useState<AnalysisPanelTabs | undefined>(undefined);

    const [selectedAnswer, setSelectedAnswer] = useState<number>(0);
    const [answers, setAnswers] = useState<[user: string, response: ChatAppResponse][]>([]);
    const [answersFiles, setAnswersFiles] = useState<string[][]>([]);
    const [streamedAnswer, setStreamedAnswer] = useState<[string, ChatAppResponse] | null>(null);
    const [steps, setSteps] = useState<{ name: string; done: boolean }[]>([]);
    const [toolCalls, setToolCalls] = useState<Record<string, { name: string; args: string; done: boolean; result?: unknown }>>({});
    const [answersActivity, setAnswersActivity] = useState<
        { steps: { name: string; done: boolean }[]; toolCalls: { id: string; name: string; args: string; done: boolean }[] }[]
    >([]);

    const [conversations, setConversations] = useState<ConversationSummary[]>([]);
    const [isConversationsLoading, setIsConversationsLoading] = useState<boolean>(false);
    const [isHistoryLoading, setIsHistoryLoading] = useState<boolean>(false);

    const [selectedKnowledgeSources, setSelectedKnowledgeSources] = useState<KnowledgeSource[]>([]);
    const [availableKnowledgeSources, setAvailableKnowledgeSources] = useState<KnowledgeSource[]>([]);

    // Dynamic filter state
    const [availableValues, setAvailableValues] = useState<Record<string, Record<string, number>>>({});
    const [totalDocuments, setTotalDocuments] = useState<number | undefined>(undefined);
    const [isLoadingFilterOptions, setIsLoadingFilterOptions] = useState<boolean>(false);

    const previousPowerplantRef = useRef<string | null>(null);
    const [isPowerplantLoading, setisPowerplantLoading] = useState(false);
    const [configLoading, setConfigLoading] = useState(true);
    const [configError, setConfigError] = useState<null | string>(null);
    const [configLoaded, setConfigLoaded] = useState(false);
    const correlationId = useRef<string | null>(null);
    const [isFeedbackOpen, setIsFeedbackOpen] = useState<boolean>(false);
    const [currentPlantImageUrl, setCurrentPlantImageUrl] = useState<string | undefined>(undefined);
    const [imageLoading, setImageLoading] = useState<boolean>(false);
    const [imageError, setImageError] = useState<boolean>(false);
    const [showPowerplantModal, setShowPowerplantModal] = useState<boolean>(false);

    const { powerplants, setPowerplants, selectedPlantId, setSelectedPlantId, selectedPlantDetails, setSelectedPlantDetails } = usePowerPlant();

    // This is needed to resize filter panel when the commands container changes size, e.g. on different screens
    const commandsContainerRef = React.useRef<HTMLDivElement | null>(null);
    const [commandsHeight, setCommandsHeight] = React.useState(0);

    const allowedHosts = ["localhost", "alexandria-dev.engie.com"];
    const isAllowedOrigin = allowedHosts.some(h => window.location.hostname === h);
    const discardKeys = isAllowedOrigin ? [] : ["elisabot", "demo"];
    // Measure the height of the commands container dynamically
    React.useLayoutEffect(() => {
        function updateHeight() {
            if (commandsContainerRef.current) {
                setCommandsHeight(commandsContainerRef.current.offsetHeight);
            }
        }

        updateHeight(); // measure on mount
        window.addEventListener("resize", updateHeight);
        return () => window.removeEventListener("resize", updateHeight);
    }, []);

    const getConfig = async () => {
        setConfigLoading(true);
        setConfigError(null);

        try {
            const config = await configApiV1(token);
            console.log(config);

            const normalizedPowerplants: Record<string, PowerplantInfo> = {};
            Object.entries(config.powerplants ?? {}).forEach(([key, value]) => {
                if (discardKeys.includes(key)) {
                    return;
                }
                if (typeof value === "string") {
                    normalizedPowerplants[key] = { id: value };
                } else {
                    normalizedPowerplants[key] = value as PowerplantInfo;
                }
            });

            setPowerplants(normalizedPowerplants);
            console.log("Normalized powerplants:", normalizedPowerplants);

            setConfigLoaded(true);
        } catch (err: any) {
            if (err instanceof ApiError) {
                if (err.status === 403) {
                    setConfigError("You are not authorized to use this service. Please contact alexandria.gen@engie.com.");
                } else {
                    setConfigError("Failed to load configuration.");
                }
            } else {
                setConfigError("Unexpected error.");
            }
        } finally {
            setConfigLoading(false);
        }
    };

    const token = useGetToken();
    const makeApiRequest = async (question: string, files?: FileAttachment[]) => {
        lastQuestionRef.current = question;
        lastQuestionFilesRef.current = files ? files.map(f => f.filename) : [];
        error && setError(undefined);
        setIsLoading(true);
        setIsStreaming(false);
        setStreamedAnswer(null);
        setSteps([]);
        setToolCalls({});
        setActiveCitation(undefined);
        setActiveAnalysisPanelTab(undefined);

        abortRef.current = new AbortController();

        try {
            const response = await fetch(`/api/v2/chat`, {
                method: "POST",
                headers: { ...getHeaders(token), "Content-Type": "application/json", Accept: "text/event-stream" },
                body: JSON.stringify({
                    stream: true,
                    thread_id: conversationId,
                    // Backend reloads prior turns from thread_id, so only the new message is sent.
                    messages: [{ role: "user", content: question, files: files && files.length > 0 ? files : undefined }],
                    context: {
                        callerApp: "alexandria_ui",
                        site: selectedPlantId ?? undefined,
                        overrides: { filter_powerplant: selectedPlantId, filters: selectedFilters },
                        knowledge_source: selectedKnowledgeSources
                    }
                }),
                signal: abortRef.current.signal
            });

            correlationId.current = response.headers.get("X-Correlation-ID");
            if (!response.ok || !response.body) {
                const dto: Partial<ErrorDTO> | null = await response.json().catch(() => null);
                throw new Error(dto?.message ?? dto?.error ?? `HTTP ${response.status}`);
            }

            const reader = response.body.getReader();
            const decoder = new TextDecoder();
            let buf = "";
            let accText = "";
            let streaming = false;
            let followups: string[] = [];
            const toolResults: { name: string; result: { description: unknown; props?: Record<string, unknown> }; args: Record<string, unknown> }[] = [];
            let sharepoint_uris: Record<string, unknown> = {};
            const citationsByName = new Map<string, Citation>();
            const localSteps: { name: string; done: boolean }[] = [];
            const localToolCalls: Record<string, { id: string; name: string; args: string; done: boolean }> = {};

            while (true) {
                const { done, value } = await reader.read();
                if (done) break;
                buf += decoder.decode(value, { stream: true });
                const lines = buf.split("\n");
                buf = lines.pop() ?? "";

                for (const line of lines) {
                    if (!line.startsWith("data:")) continue;
                    let ev: Record<string, unknown>;
                    try {
                        ev = JSON.parse(line.slice(5).trim());
                    } catch {
                        continue;
                    }

                    switch (ev.type) {
                        case "TOOL_CALL_START": {
                            const tcName = ev.toolCallName as string;
                            const tcId = ev.toolCallId as string;
                            localToolCalls[tcId] = { id: tcId, name: tcName, args: "", done: false };
                            setToolCalls(prev => ({ ...prev, [tcId]: { name: tcName, args: "", done: false } }));
                            break;
                        }
                        case "TOOL_CALL_ARGS": {
                            const tcId = ev.toolCallId as string;
                            if (localToolCalls[tcId]) localToolCalls[tcId].args += ev.delta as string;
                            setToolCalls(prev => {
                                const tc = prev[tcId];
                                if (!tc) return prev;
                                return { ...prev, [tcId]: { ...tc, args: tc.args + (ev.delta as string) } };
                            });
                            break;
                        }
                        case "TOOL_CALL_END": {
                            const tcId = ev.toolCallId as string;
                            if (localToolCalls[tcId]) localToolCalls[tcId].done = true;
                            setToolCalls(prev => {
                                const tc = prev[tcId];
                                if (!tc) return prev;
                                return { ...prev, [tcId]: { ...tc, done: true } };
                            });
                            break;
                        }
                        case "TOOL_CALL_RESULT": {
                            // Parsed out here rather than inside the setToolCalls updater: React may
                            // invoke an updater more than once, which would duplicate toolResults
                            // entries (and so the thought-process panel).
                            const tcId = ev.toolCallId as string;
                            const tc = localToolCalls[tcId];
                            if (!tc) break;
                            let parsed: unknown = ev.content;
                            try {
                                parsed = JSON.parse(ev.content as string);
                            } catch {
                                /* keep raw */
                            }
                            const parsedCitations = (parsed as { citations?: Citation[] } | null)?.citations;
                            if (Array.isArray(parsedCitations)) {
                                for (const c of parsedCitations) {
                                    if (c?.content_name) citationsByName.set(c.content_name, c);
                                    if (c?.type === CitationType.Event && c.metadata?.lorinlogs_uri) citationsByName.set(c.metadata.lorinlogs_uri, c);
                                }
                            }
                            if (typeof parsed !== "object" || parsed === null || !("description" in parsed)) {
                                let displayText = String(parsed);
                                if (tc.name === "load_skill") {
                                    try {
                                        const a = JSON.parse(tc.args);
                                        displayText = a.skill_name ?? displayText.slice(0, 100);
                                    } catch {
                                        displayText = displayText.slice(0, 100);
                                    }
                                }
                                parsed = { description: [displayText], props: {} };
                            }
                            let parsedArgs: Record<string, unknown> = {};
                            try {
                                parsedArgs = JSON.parse(tc.args);
                            } catch {
                                /* keep empty */
                            }
                            const result = parsed as { description: unknown; props?: Record<string, unknown> };
                            toolResults.push({ name: tc.name, result, args: parsedArgs });
                            setToolCalls(prev => (prev[tcId] ? { ...prev, [tcId]: { ...prev[tcId], result } } : prev));
                            break;
                        }
                        case "STEP_STARTED":
                            localSteps.push({ name: ev.stepName as string, done: false });
                            setSteps(prev => [...prev, { name: ev.stepName as string, done: false }]);
                            break;
                        case "STEP_FINISHED": {
                            const ls = localSteps.find(s => s.name === ev.stepName);
                            if (ls) ls.done = true;
                            setSteps(prev => prev.map(s => (s.name === ev.stepName ? { ...s, done: true } : s)));
                            break;
                        }
                        case "TEXT_MESSAGE_CONTENT":
                            if (!streaming) {
                                streaming = true;
                                setIsLoading(false);
                                setIsStreaming(true);
                            }
                            accText += ev.delta as string;
                            setStreamedAnswer([question, makeResponse(accText)]);
                            break;
                        case "CUSTOM":
                            if (ev.name === "followup_questions") followups = ev.value as string[];
                            if (ev.name === "alexandria_context") {
                                const ctx = ev.value as { sharepoint_uris: Record<string, unknown> };
                                sharepoint_uris = ctx.sharepoint_uris ?? {};
                            }
                            break;
                        case "RUN_ERROR":
                            throw new Error(ev.message as string);
                    }
                }
            }

            const thoughts: Thoughts[] = toolResults.map(tr => {
                const { reasoning, ...otherArgs } = tr.args;
                return {
                    title: tr.name,
                    description: tr.result.description,
                    props: {
                        ...(reasoning ? { _reasoning: reasoning } : {}),
                        ...otherArgs,
                        ...(tr.result.props ?? {})
                    } as { [key: string]: string }
                };
            });
            const final = makeResponse(accText, {
                thoughts,
                sharepoint_uris: sharepoint_uris as any,
                citations: Array.from(citationsByName.values()),
                followup_questions: followups
            });
            setAnswers(prev => [...prev, [question, final]]);
            setAnswersFiles(prev => [...prev, lastQuestionFilesRef.current]);
            setAnswersActivity(prev => [...prev, { steps: localSteps, toolCalls: Object.values(localToolCalls) }]);
            setStreamedAnswer(null);
            setSteps([]);
            setToolCalls({});
            fetchConversations(); // pick up the new/updated conversation title
        } catch (e: unknown) {
            if (e instanceof Error && e.name === "AbortError") return;
            setError(e);
        } finally {
            setIsLoading(false);
            setIsStreaming(false);
        }
    };

    const fetchConversations = async () => {
        if (!selectedPlantId) return;
        setIsConversationsLoading(true);
        try {
            const result = await listConversationsApiV2(selectedPlantId, token);
            setConversations(result);
        } catch (e) {
            console.error("Failed to load conversations:", e);
        } finally {
            setIsConversationsLoading(false);
        }
    };

    const loadConversation = async (id: string) => {
        if (!selectedPlantId || id === conversationId) return;
        abortRef.current?.abort();
        setIsHistoryLoading(true);
        error && setError(undefined);
        try {
            const messages = await getConversationMessagesApiV2(selectedPlantId, id, token);
            // Stored chronologically already; rebuild as (user, assistant) turn pairs.
            const chronological = messages;
            const pairs: [string, ChatAppResponse][] = [];
            const files: string[][] = [];
            for (let i = 0; i + 1 < chronological.length; i += 2) {
                const userMessage = chronological[i];
                const assistantMessage = chronological[i + 1];
                pairs.push([
                    userMessage.content,
                    makeResponse(assistantMessage.content, {
                        citations: assistantMessage.citations,
                        followup_questions: assistantMessage.followup_questions
                    })
                ]);
                files.push([]);
            }
            setAnswers(pairs);
            setAnswersFiles(files);
            setAnswersActivity(pairs.map(() => ({ steps: [], toolCalls: [] })));
            setStreamedAnswer(null);
            setSteps([]);
            setToolCalls({});
            setActiveCitation(undefined);
            setActiveAnalysisPanelTab(undefined);
            lastQuestionRef.current = pairs.length > 0 ? pairs[pairs.length - 1][0] : "";
            setConversationId(id);
        } catch (e) {
            setError(e);
        } finally {
            setIsHistoryLoading(false);
        }
    };

    const clearChat = () => {
        abortRef.current?.abort();
        lastQuestionRef.current = "";
        lastQuestionFilesRef.current = [];
        setConversationId(crypto.randomUUID());
        error && setError(undefined);
        setActiveCitation(undefined);
        setActiveAnalysisPanelTab(undefined);
        setAnswers([]);
        setAnswersFiles([]);
        setAnswersActivity([]);
        setStreamedAnswer(null);
        setSteps([]);
        setToolCalls({});
        setIsLoading(false);
        setIsStreaming(false);
        correlationId.current = null;
    };

    useEffect(() => {
        chatMessageStreamEnd.current?.scrollIntoView({ behavior: "smooth" });
    }, [isLoading]);

    // The reasoning panel and the answer both grow while a run is in flight. Without this,
    // from the second turn on — once .chatMessageStream is tall enough to scroll — that
    // content is appended below the fold and the view never follows it. Instant, not
    // smooth: a smooth scroll restarts its animation on every delta and never arrives.
    useEffect(() => {
        if (!isLoading && !isStreaming) return;
        const stream = chatMessageStreamEnd.current?.parentElement;
        // Leave the view alone if the user scrolled up to read something.
        if (stream && stream.scrollHeight - stream.scrollTop - stream.clientHeight > 120) return;
        chatMessageStreamEnd.current?.scrollIntoView({ block: "end" });
    }, [isLoading, isStreaming, steps, toolCalls, streamedAnswer]);

    useEffect(() => {
        getConfig();
    }, []);

    useEffect(() => {
        setShowPowerplantModal(true);
    }, []);

    useEffect(() => {
        fetchConversations();
    }, [selectedPlantId]);

    // Dynamic filtering: fetch available filter options when selectedFilters or powerplant changes
    useEffect(() => {
        if (!selectedPlantId || filters.length === 0) return;

        const fetchFilterOptions = async () => {
            setIsLoadingFilterOptions(true);
            try {
                const result: FilterOptionsResponse = await getFilterOptionsApi(selectedPlantId, selectedFilters, token);
                setAvailableValues(result.available_values);
                setTotalDocuments(result.total_documents);
            } catch (error) {
                console.error("Error fetching filter options:", error);
            } finally {
                setIsLoadingFilterOptions(false);
            }
        };

        // Small debounce to avoid rapid-fire API calls while user is clicking
        const timer = setTimeout(fetchFilterOptions, 150);
        return () => clearTimeout(timer);
    }, [selectedFilters, selectedPlantId, filters, token]);

    const onPromptTemplateChange = (_ev?: React.FormEvent<HTMLInputElement | HTMLTextAreaElement>, newValue?: string) => {
        setPromptTemplate(newValue || "");
    };

    const onTemperatureChange = (
        newValue: number,
        range?: [number, number],
        event?: React.MouseEvent | React.TouchEvent | MouseEvent | TouchEvent | React.KeyboardEvent
    ) => {
        setTemperature(newValue);
    };

    const onMinimumSearchScoreChange = (_ev?: React.SyntheticEvent<HTMLElement, Event>, newValue?: string) => {
        setMinimumSearchScore(parseFloat(newValue || "0"));
    };

    const onMinimumRerankerScoreChange = (_ev?: React.SyntheticEvent<HTMLElement, Event>, newValue?: string) => {
        setMinimumRerankerScore(parseFloat(newValue || "0"));
    };

    const onRetrieveCountChange = (_ev?: React.SyntheticEvent<HTMLElement, Event>, newValue?: string) => {
        setRetrieveCount(parseInt(newValue || "10"));
    };

    const onUseSemanticRankerChange = (_ev?: React.FormEvent<HTMLElement | HTMLInputElement>, checked?: boolean) => {
        setUseSemanticRanker(!!checked);
    };

    const onUseSemanticCaptionsChange = (_ev?: React.FormEvent<HTMLElement | HTMLInputElement>, checked?: boolean) => {
        setUseSemanticCaptions(!!checked);
    };

    const onShouldStreamChange = (_ev?: React.FormEvent<HTMLElement | HTMLInputElement>, checked?: boolean) => {
        setShouldStream(!!checked);
    };

    const onExcludeCategoryChanged = (_ev?: React.FormEvent, newValue?: string) => {
        setExcludeCategory(newValue || "");
    };

    const onUseSuggestFollowupQuestionsChange = (_ev?: React.FormEvent<HTMLElement | HTMLInputElement>, checked?: boolean) => {
        setUseSuggestFollowupQuestions(!!checked);
    };

    const onUseOidSecurityFilterChange = (_ev?: React.FormEvent<HTMLElement | HTMLInputElement>, checked?: boolean) => {
        setUseOidSecurityFilter(!!checked);
    };

    const onUseGroupsSecurityFilterChange = (_ev?: React.FormEvent<HTMLElement | HTMLInputElement>, checked?: boolean) => {
        setUseGroupsSecurityFilter(!!checked);
    };

    const onExampleClicked = (example: string) => {
        makeApiRequest(example);
    };

    const onShowCitation = (citation: string, index: number) => {
        if (activeCitation === citation && activeAnalysisPanelTab === AnalysisPanelTabs.CitationTab && selectedAnswer === index) {
            setActiveAnalysisPanelTab(undefined);
        } else {
            setActiveCitation(citation);
            setActiveAnalysisPanelTab(AnalysisPanelTabs.CitationTab);
        }

        setSelectedAnswer(index);
    };

    const onToggleTab = (tab: AnalysisPanelTabs, index: number) => {
        if (activeAnalysisPanelTab === tab && selectedAnswer === index) {
            setActiveAnalysisPanelTab(undefined);
        } else {
            setActiveAnalysisPanelTab(tab);
        }

        setSelectedAnswer(index);
    };

    const notLoggedIn = !useIsLoggedIn();
    const [reloadPowerplantReloadUiTrigger, setPowerplantUiReloadTrigger] = useState(0); // Add this

    useEffect(() => {
        if (selectedPlantId) {
            const loadPowerplantUI = async () => {
                try {
                    setisPowerplantLoading(true);
                    if (setSelectedPlantDetails) {
                        setSelectedPlantDetails({
                            id: "",
                            name: "",
                            imageUrl: undefined,
                            sharepointLibraryUrl: "",
                            exampleQuestions: []
                        });
                    }

                    const plantInfo = powerplants[selectedPlantId];
                    console.log("Selected plant info:", plantInfo);
                    if (plantInfo?.imageUrl) {
                        setImageLoading(true);
                        setImageError(false);
                        setCurrentPlantImageUrl(plantInfo.imageUrl);
                    } else {
                        setCurrentPlantImageUrl(undefined);
                        setImageLoading(false);
                    }

                    const powerplantConfig = await loadPowerplantConfigV2(selectedPlantId, token);
                    console.log("Loaded powerplant config:", powerplantConfig);

                    const imageUrl = powerplantConfig.imageUrl;
                    if (imageUrl) {
                        setImageLoading(true);
                        setCurrentPlantImageUrl(imageUrl);
                    }

                    if (setSelectedPlantDetails) {
                        setSelectedPlantDetails({
                            id: powerplantConfig.id,
                            name: powerplantConfig?.name || "",
                            imageUrl: plantInfo?.imageUrl,
                            sharepointLibraryUrl: powerplantConfig.sharepointLibraryUrl || "",
                            exampleQuestions: powerplantConfig.exampleQuestions || []
                        });
                    }
                    console.log(selectedPlantDetails);

                    setFilters([]);
                    setSelectedFilters({});
                    setSelectedKnowledgeSources(powerplantConfig.knowledgeSources);
                    setAvailableKnowledgeSources(powerplantConfig.knowledgeSources);
                    setAvailableValues({});
                    setTotalDocuments(undefined);

                    // Load new filters
                    const filters: { name: string; displayName: string; values: { text: string; key: string }[] }[] = powerplantConfig.uiFields;
                    setFilters(filters);

                    const updatedSelections: Record<string, string[]> = {};
                    filters.forEach(filter => {
                        updatedSelections[filter.name] = [];
                    });
                    setSelectedFilters(updatedSelections);

                    previousPowerplantRef.current = selectedPlantId;
                    setExampleQuestions(powerplantConfig.exampleQuestions);
                } catch (error) {
                    console.error("Error fetching powerplant configuration:", error);
                } finally {
                    setisPowerplantLoading(false);
                    setShowPowerplantModal(false);
                }
            };
            loadPowerplantUI();
        }
    }, [selectedPlantId, powerplants, reloadPowerplantReloadUiTrigger]);

    const onSettingsClick = () => {
        setIsConfigPanelOpen(prev => !prev);
    };

    const handleImageLoad = () => {
        setImageLoading(false);
        setImageError(false);
    };

    const handleImageError = () => {
        setImageLoading(false);
        setImageError(true);
        setCurrentPlantImageUrl(undefined);
    };

    const handleChangePowerplant = () => {
        clearChat();
        setShowPowerplantModal(true);
    };

    const handlePowerplantSelect = (plantId: string) => {
        if (plantId == selectedPlantId) {
            setPowerplantUiReloadTrigger(prev => prev + 1);
            return;
        }

        setSelectedPlantId(plantId);
    };

    const getToolCallLabel = (tc: { name: string; args: string }) => {
        let label = tc.name;
        try {
            const args = JSON.parse(tc.args);
            if (tc.name === "load_skill" && args.skill_name) {
                label += `: ${args.skill_name}`;
            } else {
                const first = Object.entries(args).find(([k]) => k !== "reasoning");
                if (first) label += `(${first[0]}: "${String(first[1]).slice(0, 40)}")`;
            }
        } catch {
            /* ignore */
        }
        return label;
    };

    const activeFilterCount = Object.values(selectedFilters).filter(values => Array.isArray(values) && values.length > 0).length;

    return (
        <div className={styles.container}>
            <div className={styles.commandsContainer} ref={commandsContainerRef}>
                <ClearChatButton className={styles.commandButton} onClick={clearChat} disabled={!lastQuestionRef.current || isLoading} />
                <ChangePowerplantButton className={styles.commandButton} onClick={handleChangePowerplant} title="Exit" />
            </div>
            <div className={styles.chatRoot}>
                <ConversationSidebar
                    conversations={conversations}
                    activeConversationId={conversationId}
                    isLoading={isConversationsLoading}
                    onSelect={loadConversation}
                    onNewChat={clearChat}
                />
                <div className={styles.chatContainer}>
                    {!lastQuestionRef.current ? (
                        <div className={`${styles.chatEmptyState} ${currentPlantImageUrl ? styles.hasImage : styles.noImage}`}>
                            {currentPlantImageUrl && !imageError && (
                                <div className={styles.powerplantImageContainer}>
                                    {imageLoading && <div className={styles.powerplantImageSkeleton} />}
                                    <img
                                        src={currentPlantImageUrl}
                                        alt="Powerplant"
                                        style={{ display: imageLoading ? "none" : "block" }}
                                        onLoad={handleImageLoad}
                                        onError={handleImageError}
                                    />
                                </div>
                            )}

                            <h1 className={styles.chatEmptyStateTitle}>AlexandrIA{selectedPlantDetails?.name && <> – {selectedPlantDetails.name}</>}</h1>
                            <div style={{ transform: "scale(3.5)", paddingTop: "10px", paddingBottom: "10px" }}>
                                {configError ? (
                                    <img src={forbidden_logo} alt="Chat logo" aria-hidden="true" style={{ width: "44px", height: "44px" }} />
                                ) : (
                                    <img src={logo} alt="Chat logo" aria-hidden="true" style={{ width: "22px", height: "22px" }} />
                                )}
                            </div>
                            {configLoading ? (
                                <Spinner label="Loading configuration..." />
                            ) : configError ? (
                                <div style={{ color: "red", padding: "20px", fontWeight: "bold" }}>{configError}</div>
                            ) : (
                                <ExampleList onExampleClicked={onExampleClicked} exampleQuestions={exampleQuestions} />
                            )}
                        </div>
                    ) : (
                        <div className={styles.chatMessageStream}>
                            {answers.map((answer, index) => (
                                <div key={index}>
                                    <UserChatMessage message={answer[0]} files={answersFiles[index]} />
                                    {answersActivity[index]?.steps.length > 0 && (
                                        <div style={{ padding: "8px 0 4px 16px", display: "flex", flexWrap: "wrap", gap: 6 }}>
                                            {answersActivity[index].steps.map((s, j) => (
                                                <span
                                                    key={j}
                                                    style={{
                                                        fontSize: 12,
                                                        padding: "3px 10px",
                                                        borderRadius: 12,
                                                        background: "#e6f4ea",
                                                        color: "#2e7d32",
                                                        border: "1px solid #a5d6a7"
                                                    }}
                                                >
                                                    ✓ {s.name}
                                                </span>
                                            ))}
                                        </div>
                                    )}
                                    {SHOW_TOOL_CALL_CHIPS && answersActivity[index]?.toolCalls.length > 0 && (
                                        <div style={{ padding: "2px 0 4px 16px", display: "flex", flexWrap: "wrap", gap: 4 }}>
                                            {answersActivity[index].toolCalls.map((tc, j) => (
                                                <span
                                                    key={j}
                                                    style={{
                                                        fontFamily: "monospace",
                                                        fontSize: 11,
                                                        padding: "2px 8px",
                                                        borderRadius: 4,
                                                        background: "#f0f0f0",
                                                        color: "#555",
                                                        border: "1px solid #c7d7f9",
                                                        opacity: 0.7
                                                    }}
                                                >
                                                    {getToolCallLabel(tc)}
                                                </span>
                                            ))}
                                        </div>
                                    )}
                                    {answersActivity[index]?.toolCalls.length > 0 && (
                                        <div className={styles.chatMessageGpt}>
                                            <ReasoningBlock toolCalls={answersActivity[index].toolCalls} isDone={true} />
                                        </div>
                                    )}
                                    <div className={styles.chatMessageGpt}>
                                        <Answer
                                            isStreaming={false}
                                            key={index}
                                            answer={answer[1]}
                                            isSelected={selectedAnswer === index && activeAnalysisPanelTab !== undefined}
                                            onCitationClicked={c => onShowCitation(c, index)}
                                            onThoughtProcessClicked={() => onToggleTab(AnalysisPanelTabs.ThoughtProcessTab, index)}
                                            onSupportingContentClicked={() => onToggleTab(AnalysisPanelTabs.SupportingContentTab, index)}
                                            onFollowupQuestionClicked={q => makeApiRequest(q)}
                                            showFollowupQuestions={useSuggestFollowupQuestions && answers.length - 1 === index}
                                        />
                                    </div>
                                </div>
                            ))}
                            {/* One block for the whole in-flight turn. Splitting it into separate
                                isLoading / isStreaming branches meant the first text delta unmounted
                                the ReasoningBlock and remounted a fresh one, losing its open state. */}
                            {(isLoading || isStreaming) && (
                                <div>
                                    <UserChatMessage
                                        message={streamedAnswer ? streamedAnswer[0] : lastQuestionRef.current}
                                        files={lastQuestionFilesRef.current}
                                    />
                                    {steps.length > 0 && (
                                        <div style={{ padding: "8px 0 4px 16px", display: "flex", flexWrap: "wrap", gap: 6 }}>
                                            {steps.map((s, i) => (
                                                <span
                                                    key={i}
                                                    style={{
                                                        fontSize: 12,
                                                        padding: "3px 10px",
                                                        borderRadius: 12,
                                                        background: s.done ? "#e6f4ea" : "#fff8e1",
                                                        color: s.done ? "#2e7d32" : "#795548",
                                                        border: `1px solid ${s.done ? "#a5d6a7" : "#ffe082"}`
                                                    }}
                                                >
                                                    {s.done ? "✓" : "⏳"} {s.name}
                                                </span>
                                            ))}
                                        </div>
                                    )}
                                    {SHOW_TOOL_CALL_CHIPS && Object.values(toolCalls).length > 0 && (
                                        <div style={{ padding: "2px 0 4px 16px", display: "flex", flexWrap: "wrap", gap: 4 }}>
                                            {Object.values(toolCalls).map((tc, i) => (
                                                <span
                                                    key={i}
                                                    style={{
                                                        fontFamily: "monospace",
                                                        fontSize: 11,
                                                        padding: "2px 8px",
                                                        borderRadius: 4,
                                                        background: tc.done ? "#f0f0f0" : "#e8f0fe",
                                                        color: tc.done ? "#555" : "#1a56db",
                                                        border: "1px solid #c7d7f9",
                                                        opacity: tc.done ? 0.7 : 1
                                                    }}
                                                >
                                                    {getToolCallLabel(tc)}
                                                </span>
                                            ))}
                                        </div>
                                    )}
                                    {Object.values(toolCalls).length > 0 && (
                                        <div className={styles.chatMessageGpt}>
                                            {/* Keyed per turn so collapsed/expanded state resets between questions. */}
                                            <ReasoningBlock
                                                key={answers.length}
                                                toolCalls={Object.entries(toolCalls).map(([id, tc]) => ({ id, ...tc }))}
                                                isDone={false}
                                            />
                                        </div>
                                    )}
                                    {streamedAnswer ? (
                                        <div className={styles.chatMessageGpt}>
                                            <Answer
                                                isStreaming={true}
                                                answer={streamedAnswer[1]}
                                                isSelected={false}
                                                onCitationClicked={c => onShowCitation(c, answers.length)}
                                                onThoughtProcessClicked={() => {}}
                                                onSupportingContentClicked={() => {}}
                                                onFollowupQuestionClicked={q => makeApiRequest(q)}
                                                showFollowupQuestions={false}
                                            />
                                        </div>
                                    ) : (
                                        <div className={styles.chatMessageGptMinWidth}>
                                            <AnswerLoading />
                                        </div>
                                    )}
                                </div>
                            )}
                            {error ? (
                                <>
                                    <UserChatMessage message={lastQuestionRef.current} files={lastQuestionFilesRef.current} />
                                    <div className={styles.chatMessageGptMinWidth}>
                                        <AnswerError error={error.toString()} onRetry={() => makeApiRequest(lastQuestionRef.current)} />
                                    </div>
                                </>
                            ) : null}
                            <div ref={chatMessageStreamEnd} />
                        </div>
                    )}
                    <div className={styles.chatInput}>
                        {configLoading ? null : configError ? null : (
                            <QuestionInput
                                clearOnSend
                                placeholder="Type a new question"
                                disabled={isLoading || isHistoryLoading}
                                onSend={(question, files) => makeApiRequest(question, files)}
                                onFilterToggle={onSettingsClick}
                                hasActiveFilters={activeFilterCount > 0}
                                powerplantId={selectedPlantId || undefined}
                                token={token}
                            />
                        )}
                    </div>
                </div>

                {answers.length > 0 && activeAnalysisPanelTab && (
                    <AnalysisPanel
                        className={styles.chatAnalysisPanel}
                        activeCitation={activeCitation}
                        onActiveTabChanged={x => onToggleTab(x, selectedAnswer)}
                        citationHeight="810px"
                        answer={answers[selectedAnswer][1]}
                        activeTab={activeAnalysisPanelTab}
                    />
                )}
                <button className={styles.feedbackButton} onClick={() => setIsFeedbackOpen(true)} title="Provide feedback">
                    <span className={styles.feedbackButtonInner}>
                        <Icon iconName="Chat" />
                        Feedback
                    </span>
                </button>

                <FeedbackModal
                    isOpen={isFeedbackOpen}
                    onClose={() => setIsFeedbackOpen(false)}
                    answers={answers}
                    conversationId={correlationId.current}
                    powerplant={selectedPlantId ?? ""}
                />

                <PowerplantModal
                    showPowerplantModal={showPowerplantModal}
                    configLoading={configLoading}
                    selectedPlantId={selectedPlantId}
                    powerplants={powerplants}
                    handlePowerplantSelect={handlePowerplantSelect}
                    isPowerplantLoading={isPowerplantLoading}
                />
                <Panel
                    isOpen={isConfigPanelOpen}
                    isBlocking={false}
                    onDismiss={() => setIsConfigPanelOpen(false)}
                    closeButtonAriaLabel="Close"
                    hasCloseButton={false}
                    onRenderNavigation={() => (
                        <div>
                            <div style={{ display: "flex", alignItems: "center", justifyContent: "space-between", padding: "18px 14px 12px 24px" }}>
                                <div style={{ display: "flex", alignItems: "center", gap: "6px" }}>
                                    <span style={{ fontFamily: "monospace", fontWeight: 600, fontSize: "20px", color: "rgb(50, 49, 48)" }}>Knowledge base</span>
                                    <TooltipHost content="Select which knowledge base can be selected" calloutProps={{ gapSpace: 4 }}>
                                        <IconButton
                                            iconProps={{ iconName: "Info" }}
                                            styles={{
                                                root: { width: 28, height: 28, color: "rgb(160, 158, 156)" },
                                                rootHovered: { color: "rgb(96, 94, 92)", background: "transparent" },
                                                icon: { fontSize: 16, color: "inherit" }
                                            }}
                                        />
                                    </TooltipHost>
                                </div>
                                <IconButton
                                    iconProps={{ iconName: "Cancel" }}
                                    ariaLabel="Close"
                                    onClick={() => setIsConfigPanelOpen(false)}
                                    styles={{
                                        root: { color: "rgb(96, 94, 92)" },
                                        rootHovered: { color: "rgb(50, 49, 48)", background: "rgb(243, 242, 241)" }
                                    }}
                                />
                            </div>
                            <div style={{ borderBottom: "1px solid rgb(237, 235, 233)", margin: "0 24px" }} />
                        </div>
                    )}
                    isFooterAtBottom={true}
                    styles={{
                        main: {
                            top: commandsHeight * 3
                        },
                        commands: {
                            margin: 0
                        }
                    }}
                >
                    {false && (
                        <TextField
                            className={styles.chatSettingsSeparator}
                            defaultValue={promptTemplate}
                            label="Override prompt template"
                            multiline
                            autoAdjustHeight
                            onChange={onPromptTemplateChange}
                        />
                    )}
                    {false && (
                        <Slider
                            className={styles.chatSettingsSeparator}
                            label="Temperature"
                            min={0}
                            max={1}
                            step={0.1}
                            defaultValue={temperature}
                            onChange={onTemperatureChange}
                            showValue
                            snapToStep
                        />
                    )}
                    {false && (
                        <SpinButton
                            className={styles.chatSettingsSeparator}
                            label="Minimum search score"
                            min={0}
                            step={0.01}
                            defaultValue={minimumSearchScore.toString()}
                            onChange={onMinimumSearchScoreChange}
                        />
                    )}
                    {false && (
                        <SpinButton
                            className={styles.chatSettingsSeparator}
                            label="Minimum reranker score"
                            min={1}
                            max={4}
                            step={0.1}
                            defaultValue={minimumRerankerScore.toString()}
                            onChange={onMinimumRerankerScoreChange}
                        />
                    )}
                    {false && (
                        <SpinButton
                            className={styles.chatSettingsSeparator}
                            label="Retrieve this many search results:"
                            min={1}
                            max={50}
                            defaultValue={retrieveCount.toString()}
                            onChange={onRetrieveCountChange}
                        />
                    )}
                    {false && <TextField className={styles.chatSettingsSeparator} label="Exclude category" onChange={onExcludeCategoryChanged} />}
                    {false && (
                        <Checkbox
                            className={styles.chatSettingsSeparator}
                            checked={useSemanticCaptions}
                            label="Use query-contextual summaries instead of whole documents"
                            onChange={onUseSemanticCaptionsChange}
                            disabled={!useSemanticRanker}
                        />
                    )}
                    {false && (
                        <Checkbox
                            className={styles.chatSettingsSeparator}
                            checked={useSuggestFollowupQuestions}
                            label="Suggest follow-up questions"
                            onChange={onUseSuggestFollowupQuestionsChange}
                        />
                    )}
                    {false && (
                        <FilterPowerplantSettings
                            filterPowerplantInputs={selectedPlantId || ""}
                            updateFilterPowerplant={inputs => {
                                setSelectedPlantId(inputs);
                            }}
                            powerplants={powerplants}
                            label="Select Powerplant"
                        />
                    )}
                    {isPowerplantLoading ? (
                        <Spinner label="Loading filters..." />
                    ) : (
                        <FilterSettings
                            filters={filters}
                            selectedFilters={selectedFilters}
                            updateFilters={setSelectedFilters}
                            selectedKnowledgeSources={selectedKnowledgeSources}
                            knowledgeSources={availableKnowledgeSources}
                            onKnowledgeSourcesChange={setSelectedKnowledgeSources}
                            onClearFilters={() => {
                                const clearedFilters: Record<string, string[]> = {};
                                filters.forEach(filter => {
                                    clearedFilters[filter.name] = [];
                                });
                                setSelectedFilters(clearedFilters);
                            }}
                            availableValues={availableValues}
                            totalDocuments={totalDocuments}
                            isLoadingFilterOptions={isLoadingFilterOptions}
                        />
                    )}
                    {false && (
                        <Checkbox
                            className={styles.chatSettingsSeparator}
                            checked={useOidSecurityFilter}
                            label="Use oid security filter"
                            disabled={notLoggedIn}
                            onChange={onUseOidSecurityFilterChange}
                        />
                    )}
                    {false && (
                        <Checkbox
                            className={styles.chatSettingsSeparator}
                            checked={useGroupsSecurityFilter}
                            label="Use groups security filter"
                            disabled={notLoggedIn}
                            onChange={onUseGroupsSecurityFilterChange}
                        />
                    )}
                    {false && (
                        <Checkbox
                            className={styles.chatSettingsSeparator}
                            checked={shouldStream}
                            label="Stream chat completion responses"
                            onChange={onShouldStreamChange}
                        />
                    )}
                </Panel>
            </div>
        </div>
    );
};

export default ChatHistory;
